import React, { useEffect, useMemo, useState } from "react";
import { Button, Alert, Image } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAppContext } from "../Context/AppContext";
import { isLoggedIn } from "../auth/authStorage";
import { explorerOnboardingQueryKey } from "../auth/authSession";
import { useGetUser } from "../api/queries/useGetUser";
import { getMyExplorerOnboarding } from "../api/explorerOnboardingApi";
import ProfileModal from "./common/ProfileModal";
import { User } from "../types/Types";
import { resolveProfessionalPhotoUrl } from "../utils/profilePhoto";
import CurvedTextOverlay from "./Account/CurvedTextOverlay";
import { FaUniversity } from "react-icons/fa";
import { MdOutlineBusiness } from "react-icons/md";
import {
  FiCalendar,
  FiCreditCard,
  FiEdit,
  FiMail,
  FiMessageSquare,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";
import { useTranslateDynamicFields } from "../api/queries/useTranslateDynamicFields";
import {
  isEarlyStageMembership,
  isLegacyYouthMembership,
} from "../utils/membershipTier";
import { getMembershipProfileBadgeTheme } from "../utils/membershipIdentity";
import DashboardCard, {
  type DashboardCardProps,
  type DashboardCardVariant,
} from "./DashboardCard";

const formatNamePart = (name: string) =>
  name ? name.charAt(0).toUpperCase() + name.slice(1) : "";

type DashboardCardIcon =
  | "profile"
  | "connections"
  | "bookedEvents"
  | "messages"
  | "notes"
  | "contact"
  | "membership";

type DashboardCardConfig = {
  id: string;
  variant: DashboardCardVariant;
  titleKey: string;
  textKey: string;
  link: string;
  icon: DashboardCardIcon;
  statusKey: string;
  statusTone: DashboardCardProps["status"]["tone"];
  ctaKind: "profile" | "navigate";
};

const cardData: DashboardCardConfig[] = [
  {
    id: "profile",
    variant: "default",
    titleKey: "dashboard.cards.profile.title",
    textKey: "dashboard.cards.profile.text",
    link: "",
    icon: "profile",
    statusKey: "dashboard.status.published",
    statusTone: "live",
    ctaKind: "profile",
  },
  {
    id: "connections",
    variant: "default",
    titleKey: "dashboard.cards.connections.title",
    textKey: "dashboard.cards.connections.text",
    link: "/connections",
    icon: "connections",
    statusKey: "dashboard.status.open",
    statusTone: "pending",
    ctaKind: "navigate",
  },
  {
    id: "booked-events",
    variant: "default",
    titleKey: "dashboard.cards.bookedEvents.title",
    textKey: "dashboard.cards.bookedEvents.text",
    link: "/event-booking",
    icon: "bookedEvents",
    statusKey: "dashboard.status.open",
    statusTone: "pending",
    ctaKind: "navigate",
  },
  {
    id: "messages",
    variant: "default",
    titleKey: "dashboard.cards.messages.title",
    textKey: "dashboard.cards.messages.text",
    link: "/messages",
    icon: "messages",
    statusKey: "dashboard.status.inbox",
    statusTone: "new",
    ctaKind: "navigate",
  },
  {
    id: "notes",
    variant: "default",
    titleKey: "dashboard.cards.notes.title",
    textKey: "dashboard.cards.notes.text",
    link: "/attendee-notes",
    icon: "notes",
    statusKey: "dashboard.status.open",
    statusTone: "pending",
    ctaKind: "navigate",
  },
  {
    id: "contact",
    variant: "default",
    titleKey: "dashboard.cards.contact.title",
    textKey: "dashboard.cards.contact.text",
    link: "/contact-us",
    icon: "contact",
    statusKey: "dashboard.status.open",
    statusTone: "pending",
    ctaKind: "navigate",
  },
  {
    id: "manage-membership",
    variant: "default",
    titleKey: "dashboard.cards.manageMembership.title",
    textKey: "dashboard.cards.manageMembership.text",
    link: "/manage-membership",
    icon: "membership",
    statusKey: "dashboard.status.open",
    statusTone: "pending",
    ctaKind: "navigate",
  },
];

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const navigate = useNavigate();
  const { setUser, objectId } = useAppContext();
  const {
    data: userData,
    isPending,
    isFetching,
  } = useGetUser({
    id: objectId || "",
    enabled: !!objectId,
  });

  const { data: explorerMe } = useQuery({
    queryKey: explorerOnboardingQueryKey(objectId),
    queryFn: getMyExplorerOnboarding,
    enabled: isLoggedIn() && !!objectId,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (userData) {
      setUser(userData);
    }
  }, [userData, setUser]);

  const selectedUser = { ...userData?.user, ...userData?.imageUrls };
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const isLgTablet = useMediaQuery({ minWidth: 992, maxWidth: 1023 });
  const isMdTablet = useMediaQuery({ minWidth: 768, maxWidth: 991 });
  const imageSize = isDesktop ? 150 : isLgTablet ? 140 : isMdTablet ? 130 : 95;

  const dashboardFullName = useMemo(() => {
    if (!userData?.user) return "";
    const first = formatNamePart(userData.user.firstName || "");
    const last = formatNamePart(userData.user.lastName || "");
    return `${first} ${last}`.trim();
  }, [userData?.user]);

  const dashboardRoleLine = useMemo(() => {
    const u = userData?.user;
    if (!u) return "";
    if (
      u.membershipName === "Youth Membership" ||
      u.membershipName === "Early Stage Membership"
    ) {
      return u.studyInterest ? formatNamePart(u.studyInterest) : "";
    }
    return u.personalPositionCompany
      ? formatNamePart(u.personalPositionCompany)
      : "";
  }, [userData?.user]);

  const dashboardSummaryFields = useMemo(() => {
    const u = userData?.user;
    if (!u) return {};
    const fields: Record<string, string> = {};
    if (dashboardFullName) fields.fullName = dashboardFullName;
    if (dashboardRoleLine) fields.roleLine = dashboardRoleLine;
    const sector = u.businessSectorIndustry?.trim();
    if (sector) fields.sector = sector;
    const businessName = u.businessName?.trim();
    if (businessName) fields.businessName = businessName;
    return fields;
  }, [userData?.user, dashboardFullName, dashboardRoleLine]);

  const { data: translatedDashboardSummary } = useTranslateDynamicFields(
    dashboardSummaryFields,
    i18n.resolvedLanguage || i18n.language
  );

  const displayDashboardName =
    translatedDashboardSummary?.fullName?.trim() || dashboardFullName;
  const displayDashboardRole =
    translatedDashboardSummary?.roleLine?.trim() || dashboardRoleLine;
  const displayDashboardSector =
    translatedDashboardSummary?.sector?.trim() ||
    userData?.user?.businessSectorIndustry ||
    "";
  const displayDashboardBusinessName =
    translatedDashboardSummary?.businessName?.trim() ||
    userData?.user?.businessName ||
    "";
  const isEarlyStageMember = isEarlyStageMembership(selectedUser?.membershipName);
  const isLegacyYouthMember = isLegacyYouthMembership(selectedUser?.membershipName);
  const isYouthLikeMember = isEarlyStageMember || isLegacyYouthMember;
  const membershipBadgeTheme = useMemo(
    () => getMembershipProfileBadgeTheme(selectedUser),
    [selectedUser],
  );
  const displayDashboardMetaLine = (
    isYouthLikeMember
      ? [displayDashboardRole, displayDashboardSector]
      : [
          displayDashboardRole,
          displayDashboardBusinessName,
          displayDashboardSector,
        ]
  )
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" · ");

  const dashboardDateLabel = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(new Date());
    } catch {
      return new Date().toLocaleDateString();
    }
  }, [i18n.language, i18n.resolvedLanguage]);

  const firstName =
    formatNamePart(userData?.user?.firstName || "") ||
    displayDashboardName.split(" ")[0] ||
    "";

  const renderCardIcon = (card: DashboardCardConfig) => {
    const size = 28;
    switch (card.icon) {
      case "profile":
        return <FiUser size={size} aria-hidden />;
      case "connections":
        return <FiUsers size={size} aria-hidden />;
      case "bookedEvents":
        return <FiCalendar size={size} aria-hidden />;
      case "messages":
        return <FiMessageSquare size={size} aria-hidden />;
      case "notes":
        return <FiEdit size={size} aria-hidden />;
      case "contact":
        return <FiMail size={size} aria-hidden />;
      case "membership":
        return <FiCreditCard size={size} aria-hidden />;
      default:
        return null;
    }
  };

  if (!objectId || isPending || isFetching || !userData?.user) {
    return (
      <div className="container p-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t("dashboard.loading")}</span>
        </div>
      </div>
    );
  }

  const explorerPayload = explorerMe?.payload;
  const showExplorerEvents = false;

  return (
    <div className="w-100 pb-4 min-w-0">
      {showExplorerEvents && (
        <Alert
          variant="info"
          className="mb-4 rounded-4 border-0 shadow-sm"
        >
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div>
              <strong>Explorer</strong>
              {explorerPayload?.badge ? (
                <span className="badge bg-primary ms-2">{explorerPayload.badge}</span>
              ) : null}
              <p className="mb-0 small mt-1 text-secondary">
                {t("dashboard.explorerInfo")}
              </p>
            </div>
            <Button
              variant="primary"
              className="text-nowrap"
              onClick={() => navigate("/event-booking")}
            >
              {t("dashboard.viewEvents")}
            </Button>
          </div>
        </Alert>
      )}

      <div className="card shadow-sm border-0 pb-2 overflow-hidden">
        <div>
          <div
            className={`${
              selectedUser.BannerImage ? "" : "bg-project-gradient"
            } position-relative profile-banner d-flex justify-content-center align-items-center`}
            style={{
              backgroundImage: `url(${selectedUser?.BannerImage ?? ""})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              width: "100%",
              height: "100%",
              aspectRatio: "3 / 1",
            }}
          >
            {!selectedUser.BannerImage && (
              <div className="d-flex h-100 w-100 justify-content-center align-items-center">
                <img
                  src="/SMBF-logo-white.png"
                  alt={t("dashboard.logoAlt")}
                  className="w-25 h-25"
                />
              </div>
            )}{" "}
            <div className="position-absolute translate-middle image-container">
              <div className="position-relative w-100 h-100">
                <Image
                  src={resolveProfessionalPhotoUrl(selectedUser, "/user.png")}
                  roundedCircle
                  alt={t("dashboard.profilePhotoAlt")}
                  className="profile-image w-100 h-100 shadow-sm border border-2 border-white p-1"
                />

                {membershipBadgeTheme && (
                  <span className="badge-overlay text-white fw-bold px-2 py-1 rounded-pill">
                    <CurvedTextOverlay
                      size={imageSize}
                      stroke={membershipBadgeTheme.stroke}
                      text={membershipBadgeTheme.text}
                    />
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="mx-3 pt-5 flex-grow-1">
            <div className="d-flex justify-content-between align-items-center">
              <div className="mt-md-4 mt-lg-5">
                <h2 className="mb-0">{displayDashboardName}</h2>
                {displayDashboardMetaLine ? (
                  <span className="text-muted">{displayDashboardMetaLine}</span>
                ) : null}
              </div>
              <div className="d-none d-md-block">
                {isLegacyYouthMember || isEarlyStageMember ? (
                  <div className="fw-semibold d-flex gap-1 align-items-center">
                    <FaUniversity size={25} />
                    {selectedUser.universityCollege}
                  </div>
                ) : (
                  <div className="d-flex gap-3 align-items-center cursor-pointer">
                    {selectedUser.businessLogo ? (
                      <img
                        src={selectedUser.BusinessLogo}
                        className="rounded-circle"
                        width={40}
                        height={40}
                        alt={
                          displayDashboardBusinessName ||
                          t("dashboard.logoAlt")
                        }
                      />
                    ) : (
                      <MdOutlineBusiness size={30} />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-between gap-3 w-100 min-w-0 p-3 p-sm-4 mb-4 mt-4 rounded-4 shadow-sm"
        style={{ backgroundColor: "var(--smbf-navy-deep, #031b3d)" }}
      >
        <div className="flex-grow-1 min-w-0">
          <h2 className="h5 mb-0 text-white fw-bold text-truncate">
            {t("dashboard.welcomeBack", {
              firstName: firstName || t("dashboard.welcomeBackFallback"),
            })}
          </h2>
          <p
            className="mb-0 mt-1 small text-truncate"
            style={{ color: "#94a3b8" }}
          >
            {dashboardDateLabel}
          </p>
        </div>
        <Button
          type="button"
          variant="primary"
          className="flex-shrink-0 text-nowrap align-self-stretch align-self-sm-auto"
          onClick={() => navigate("/event-booking")}
        >
          {t("dashboard.bookAnEvent")}
        </Button>
      </div>

      <div className="row g-4">
        {cardData.map((card) => {
          const ctaLabel =
            card.ctaKind === "profile"
              ? t("dashboard.profilePreview")
              : t("dashboard.goTo", { item: t(card.titleKey) });

          return (
            <div key={card.id} className="col-12 col-lg-4">
              <DashboardCard
                variant={card.variant}
                icon={renderCardIcon(card)}
                status={{
                  label: t(card.statusKey),
                  tone: card.statusTone,
                }}
                title={t(card.titleKey)}
                description={t(card.textKey)}
                ctaLabel={ctaLabel}
                ctaHref={card.ctaKind === "navigate" ? card.link : undefined}
                onCtaClick={() => {
                  if (card.ctaKind === "profile") {
                    setShowProfileModal(true);
                    return;
                  }
                  navigate(card.link);
                }}
              />
            </div>
          );
        })}
      </div>
      {showProfileModal && (
        <ProfileModal
          isConnected
          show={showProfileModal}
          onHide={() => setShowProfileModal(false)}
          selectedUser={{ ...userData?.user, ...userData?.imageUrls } as User}
        />
      )}
    </div>
  );
};

export default Dashboard;
